import React, { useState, useEffect } from "react";
import { Card, Form, Button, Row, Col, ProgressBar, InputGroup, FormControl, Spinner } from "react-bootstrap";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import { ChargingBASE_URL } from '../../Config';

const UploadFileForReversal = () => {
  const [file, setFile] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [isReversalLoading, setIsReversalLoading] = useState(false); // Added state for reversal loading

  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    if (user) {
      setUserId(user.userId);
    } else {
      setError("User is not logged in. Please log in.");
    }
  }, []);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file to upload.");
      return;
    }

    if (!userId) {
      setError("User ID is missing. Please log in.");
      return;
    }

    const formData = new FormData();
    formData.append("ExcelFile", file);
    formData.append("UserId", userId);

    const url = `${ChargingBASE_URL}UploadFile_Reversal`;

    try {
      setIsLoading(true);
      setError(null);
      setProgress(10); // Start progress

      const response = await fetch(url, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setResponseData(data);

        // Simulate progress update
        const progressInterval = setInterval(() => {
          setProgress((prevProgress) => {
            if (prevProgress >= 100) {
              clearInterval(progressInterval);
              return 100;
            }
            return prevProgress + 10;
          });
        }, 200);
      } else {
        throw new Error("Failed to upload file.");
      }
    } catch (error) {
      setError(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle the reversal POST request with loading state
  const handleReversal = async (transactionId, id) => {
    if (!userId || !transactionId || !id) return;

    setIsReversalLoading(true); // Set the loading state to true when the reversal starts

    const url = `${ChargingBASE_URL}JazzCash_Reversal`;
    const requestData = {
      userId: userId,
      originalTransactionId: transactionId,
      id: id,
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
      } else {
        throw new Error("Reversal request failed");
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setIsReversalLoading(false); // Set the loading state to false once the request completes
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredData = responseData
    ? responseData.response.filter((item) => {
        return (
          item.agentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.transactionId.toLowerCase().includes(searchQuery.toLowerCase())||
          item.status.toLowerCase().includes(searchQuery.toLowerCase())
        );
      })
    : [];

  const columns = [
    { name: "Id", selector: (row) => row.id, sortable: true },
    { name: "Agent Name", selector: (row) => row.agentName, sortable: true },
    { name: "Company Name", selector: (row) => row.companyName, sortable: true },
    { name: "Timestamp", selector: (row) => row.timestamp, sortable: true },
    { name: "Reason", selector: (row) => row.reason, sortable: true },
    { name: "Amount", selector: (row) => row.amount, sortable: true },
    { name: "Account Number", selector: (row) => row.account_Number, sortable: true },
    { name: "Status", selector: (row) => row.status, sortable: true },
    { name: "Transaction ID", selector: (row) => row.transactionId, sortable: true },
    {
      name: "Action",
      cell: (row) => {
        if (row.status === "Sub") {
          return (
            <Button
              variant="danger"
              onClick={() => handleReversal(row.transactionId, row.id)}
              disabled={isReversalLoading} // Disable the button when reversal is loading
            >
              {isReversalLoading ? <Spinner animation="border" size="sm" /> : "Reversal"}
            </Button>
          );
        }
        return null;
      },
      sortable: false,
    },
  ];

  const handleSort = (column, sortDirection) => {
    setSortDirection(sortDirection);
    const sortedData = [...filteredData].sort((a, b) => {
      if (sortDirection === "asc") {
        return a[column.selector] > b[column.selector] ? 1 : -1;
      } else {
        return a[column.selector] < b[column.selector] ? 1 : -1;
      }
    });
    setResponseData((prevState) => ({ ...prevState, response: sortedData }));
  };

  return (
    <div className="container mt-1">
      <Row className="justify-content-center">
        <Col lg={12} md={12} sm={12}>
          <Card className="shadow-lg">
            {/* <Card.Header>
              <h5 className="text-center">Upload Excel File for Reversal</h5>
            </Card.Header> */}
            <Card.Body>
              <Form>
                <Row className="mb-2">
                  <Col md={8}>
                    <Form.Group controlId="excelFile">
                      <Form.Control
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Button
                      variant="primary"
                      onClick={handleUpload}
                      className="w-100"
                      disabled={isLoading}
                    >
                      {isLoading ? "Uploading..." : "Upload File"}
                    </Button>
                  </Col>

                  {isLoading && (
                    <div className="text-center mt-3">
                      <ProgressBar animated now={progress} label={`Uploading... ${progress}%`} />
                    </div>
                  )}

                  {error && <div className="text-danger text-center mt-2">{error}</div>}
                </Row>
              </Form>
            </Card.Body>
          </Card>

          {responseData && responseData.responseCode === "0000" && (
            <Card className="shadow-lg">
              <Card.Header>
                <Row>
                  <Col lg={8} md={10} sm={12}>
                    <h6 className="text-center">Processed Transactions</h6>
                  </Col>
                  <Col lg={4} md={2} sm={12}>
                    <InputGroup>
                      <FormControl
                        placeholder="Search....."
                        value={searchQuery}
                        onChange={handleSearchChange}
                      />
                    </InputGroup>
                  </Col>
                </Row>
              </Card.Header>
              <Card.Body>
                <DataTable
                  columns={columns}
                  data={filteredData}
                  pagination
                  paginationPerPage={10}
                  paginationRowsPerPageOptions={[5, 10, 15, 20]}
                  highlightOnHover
                  sortServer
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  persistTableHead
                />
                <div className="text-center text-success mt-1">
                  {responseData.message}
                </div>

                <div className="text-center mt-1">
                  <CSVLink
                    data={responseData.response.map((item) => ({
                      Id: item.id,
                      AgentName: item.agentName,
                      CompanyName: item.companyName,
                      Timestamp: item.timestamp,
                      Reason: item.reason,
                      Amount: item.amount,
                      AccountNumber: item.account_Number,
                      Status: item.status,
                      TransactionId: item.transactionId,
                    }))}
                    filename={"ReversalData.csv"}
                  >
                    <Button variant="success" className="w-100">
                      Download CSV
                    </Button>
                  </CSVLink>
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default UploadFileForReversal;
